import { useCallback, useEffect, useState } from 'react';
import { getRefreshToken, getToken } from '../authentication/authProvider';

/**
 * Where the remains are and the earliest pickup, as the server has them (T-230).
 * The admin never works this out itself, for the same reason as the ship day:
 * the sidebar, the date fields and the family's page must always agree.
 */
export interface RemainsStep {
  key: string;
  text: string;
  state: 'past' | 'current' | 'next';
}

export interface RemainsLineData {
  steps: RemainsStep[];
  problem: string | null;
  canMarkReceived: boolean;
  canUndoReceived: boolean;
  receivedBy: string | null;
}

export interface OfficePickupRule {
  earliestDay: string;
  soonestTransferDay: string | null;
  reason: string | null;
}

export interface RemainsPlan {
  line: RemainsLineData | null;
  officePickup: OfficePickupRule | null;
}

const url = (funeralId: number | string, path: string) =>
  `${(process.env.REACT_APP_PAPERMAN_API_URL || 'http://localhost:3001').replace(/\/$/, '')}/delivery-scheduling/staff/${funeralId}/remains${path}`;

const headers = async () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${await getToken()}`,
  'Refresh-Token': await getRefreshToken(),
});

const post = async (funeralId: number | string, path: string, body?: unknown) => {
  const response = await fetch(url(funeralId, path), {
    method: 'POST',
    headers: await headers(),
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!response.ok) {
    const raw = await response.text().catch(() => '');
    let detail = raw;
    try {
      detail = (JSON.parse(raw) as { message?: string }).message ?? raw;
    } catch {
      // Not JSON: the raw text is the best there is.
    }
    throw new Error(detail || `Request failed (${response.status})`);
  }
  return response.json();
};

export const markReceivedAtOffice = (funeralId: number | string) => post(funeralId, '/received');
export const undoReceivedAtOffice = (funeralId: number | string) => post(funeralId, '/undo-received');
export const recordDayOverride = (
  funeralId: number | string,
  body: { day: string; earliest: string; reason: string },
) => post(funeralId, '/day-override', body);

/**
 * `watch` refetches when something the plan depends on changes on the case. A
 * failed fetch leaves the plan null: the line hides and no date is held back,
 * because being unable to ask must not stop staff from saving a case.
 */
export const useRemainsPlan = (
  funeralId: number | string | null | undefined,
  watch = '',
  enabled = true,
): { plan: RemainsPlan | null; reload: () => void } => {
  const [plan, setPlan] = useState<RemainsPlan | null>(null);
  const [tick, setTick] = useState(0);
  const reload = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!enabled || funeralId == null) return;
    let live = true;
    void (async () => {
      try {
        const response = await fetch(url(funeralId, ''), { headers: await headers() });
        const next = response.ok ? ((await response.json()) as RemainsPlan) : null;
        if (live) setPlan(next);
      } catch {
        if (live) setPlan(null);
      }
    })();
    return () => {
      live = false;
    };
  }, [funeralId, watch, enabled, tick]);

  return { plan, reload };
};
